import React, { useEffect, useRef, useState } from "react";
import { Button, Column, Icon, Row, Tab, TabGroup, Text } from "@amzn/meridian";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/store";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import { COLORS } from "src/constants/colors";
import CameraFrame from "./cameraFrame";
import {
  setActiveQuestion,
  setInspectionResults,
  setReadyToSubmitReport,
  setSelectedInspectionTemplate,
  updateInspectionResult,
} from "src/store/dataSlice";
import ResultDisplayCard from "src/components/UI/resultDisplayCard";
import { Evidence } from "src/data/types";
import SubmitReport from "./submitReport";
export type InspectionOverrideData = {
  overrideResult: string;
  reasonCode: string;
  evidence: Evidence;
  comment?: string;
};
const Inspection = () => {
  const {
    inspectionTemplates,
    selectedInspectionTemplate,
    activeQuestion,
    inspectionResults,
    imageCapturingMode,
    readyToSubmitReport,
    currentCreatedInspection,
  } = useAppSelector((state) => state.data);
  const [activeQuestionId, setActiveQuestionId] = useState(
    selectedInspectionTemplate?.questions.find(
      (q) => q.questionId === activeQuestion?.questionId
    )?.questionId || ""
  );
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(
    selectedInspectionTemplate?.questions.findIndex(
      (obj) => obj.questionId === activeQuestion?.questionId
    ) || 0
  );
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const navigate = useNavigate();
  const { templateId } = useParams();
  const dispatch = useAppDispatch();
  const totalQuestions = selectedInspectionTemplate?.questions?.length;

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw Error("Camera not supported in this broweser");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play().catch(() => {});
    } catch (e: any) {
      const name = e?.name || "";
      if (name === "NotAllowedError") {
        throw Error("Camera access was denied. Allow camera in settings.");
      } else if (name === "NotFoundError") {
        throw Error("No camera found on this device.");
      } else {
        throw Error(`Camera error: ${e?.message || String(e)}`);
      }
    }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  };
  const submitReport = async () => {
    if (!selectedInspectionTemplate) return;
    try {
      for (const result of inspectionResults) {
        const resultToUpdate = new FormData();
        resultToUpdate.append("ai_result", result.aiResult);
        resultToUpdate.append("confirmed_result", result.confirmedResult);
        resultToUpdate.append("is_compliant", String(result.isCompliant));
        resultToUpdate.append(
          "is_inspector_override",
          String(result.isInspectorOverride)
        );
        if (result.overrideReason) {
          resultToUpdate.append("override_reason", result.overrideReason);
        }
        for (const evidence of result.evidences) {
          if (evidence?.file) resultToUpdate.append("files", evidence.file);
        }

        await dispatch(
          updateInspectionResult({
            inspection_id: currentCreatedInspection.inspectionId,
            question_id: result.questionId,
            formData: resultToUpdate,
          })
        ).unwrap();
        navigate("/completedInspections");
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (imageCapturingMode) {
      startCamera();
    } else if (!imageCapturingMode || readyToSubmitReport) {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [imageCapturingMode, readyToSubmitReport]);

  useEffect(() => {
    if (!selectedInspectionTemplate && templateId) {
      const template = inspectionTemplates.find(
        (temp) => temp.templateId === templateId
      );
      if (template) {
        dispatch(setSelectedInspectionTemplate(template));
        navigate(`/templates`);
      }
    }
  }, [selectedInspectionTemplate, templateId, inspectionTemplates]);

  useEffect(() => {
    dispatch(
      setActiveQuestion(
        selectedInspectionTemplate?.questions[activeQuestionIndex]
      )
    );
  }, [activeQuestionIndex, selectedInspectionTemplate]);

  useEffect(() => {
    const activeQ = selectedInspectionTemplate?.questions.find(
      (q) => q.questionId === activeQuestionId
    );
    if (activeQuestion) dispatch(setActiveQuestion(activeQ));
  }, [activeQuestionId, selectedInspectionTemplate]);

  return readyToSubmitReport ? (
    <SubmitReport />
  ) : (
    <Column
      className="inspection-page"
      height="100vh"
      alignmentVertical="justify"
      backgroundColor={COLORS.VERY_DARK_GRAY}
    >
      <Column
        width="100%"
        spacingInset="600 500 400"
        className="inspection-info-wrapper"
      >
        <Row>
          <div
            style={{ cursor: "pointer", marginLeft: "50px" }}
            onClick={() => {
              dispatch(setInspectionResults([]));
              navigate(`/templates/${selectedInspectionTemplate?.templateId}`);
            }}
          >
            <Icon tokens={chevronLeftLargeTokens} color="inverted" />
          </div>
          {selectedInspectionTemplate?.templateName && (
            <Text color="inverted" type="b100">
              {selectedInspectionTemplate?.templateName}
            </Text>
          )}
        </Row>
        <Column spacing={"300"}>
          <Row>
            <Column spacing={"300"}>
              <TabGroup
                value={activeQuestion?.questionId}
                onChange={(value) => {
                  setActiveQuestionId(value);
                }}
                type="default"
              >
                {selectedInspectionTemplate?.questions?.map((question) => (
                  <Tab key={question.questionId} value={question.questionId}>
                    <div />
                  </Tab>
                ))}
              </TabGroup>
              <Row>
                <Text color="inverted">{`Question ${
                  activeQuestionIndex + 1
                } / ${totalQuestions}`}</Text>
              </Row>
            </Column>
          </Row>
          <Row spacingInset={"200 none"}>
            <Text color="inverted">{activeQuestion?.questionText}</Text>
          </Row>
        </Column>
      </Column>
      <CameraFrame
        videoRef={videoRef}
        activeQuestionIndex={activeQuestionIndex}
        totalQuestions={totalQuestions}
        setActiveQuestionId={setActiveQuestionId}
        setActiveQuestionIndex={setActiveQuestionIndex}
      />
    </Column>
  );
};

export default Inspection;
